export const PAGE_SIZE = 24
export const pagePrefix = groupId => `page:${encodeURIComponent(groupId)}:`
export const pageKey = query => `${pagePrefix(query.groupId)}${query.assetType || 'all'}:${query.realPerson ?? 'all'}:${query.page || 1}:${query.size || PAGE_SIZE}${query.keyword?.trim() ? `:q=${encodeURIComponent(query.keyword.trim())}` : ''}`

// Plain-data cache: safe to share across component instances, without retaining Vue/DOM objects.
export function createAssetLibraryCache({ storage, scope, session, now = Date.now,
  ttl = 180000, retention = 86400000, maxEntries = 120, maxChars = 2000000,
  storageKey = 'volc-asset-library:v3' }) {
  let context = null
  let entries = new Map()
  let pending = new Map()
  let versions = new Map()
  let navigation = {}
  let persistTimer = null

  const ensureContext = () => {
    let account, credential
    try { account = String(scope() || ''); credential = session() } catch (_) { /* unavailable */ }
    if (context?.account === account && context?.credential === credential) return context
    const previous = context
    context = { account, credential }
    entries = new Map(); pending = new Map(); versions = new Map(); navigation = {}
    if (persistTimer) clearTimeout(persistTimer)
    persistTimer = null
    try {
      // A login change must not reuse data authorized under the previous session.
      if (previous || !account || !credential) storage()?.removeItem(storageKey)
      const raw = storage()?.getItem(storageKey)
      const saved = raw && raw.length <= maxChars ? JSON.parse(raw) : null
      if (account && credential && saved?.account === account && Array.isArray(saved.entries)) {
        entries = new Map(saved.entries.filter(([key, entry]) => typeof key === 'string' &&
          entry && Number.isFinite(entry.at) && now() - entry.at < retention).slice(-maxEntries))
        navigation = saved.navigation && typeof saved.navigation === 'object' ? saved.navigation : {}
      }
    } catch (_) { /* Blocked/corrupt storage is a cache miss. */ }
    return context
  }
  const flush = () => {
    ensureContext()
    if (persistTimer) clearTimeout(persistTimer)
    persistTimer = null
    if (!context.account || !context.credential) return
    try {
      const serialize = () => JSON.stringify({ account: context.account, navigation, entries: [...entries] })
      let serialized = serialize()
      while (serialized.length > maxChars && entries.size > 0) {
        entries.delete(entries.keys().next().value)
        serialized = serialize()
      }
      if (serialized.length <= maxChars) storage()?.setItem(storageKey, serialized)
    } catch (_) { /* Quota failures must not fail a successful server read. */ }
  }
  const persist = () => {
    if (!persistTimer) persistTimer = setTimeout(flush, 120)
  }
  const peek = key => {
    ensureContext()
    const entry = entries.get(key)
    if (!entry) return null
    if (now() - entry.at >= retention) { entries.delete(key); return null }
    entries.delete(key); entries.set(key, entry)
    return { data: entry.data, fresh: now() - entry.at < ttl }
  }
  const invalidate = (prefix = '') => {
    ensureContext()
    for (const key of new Set([...entries.keys(), ...pending.keys()])) {
      if (!key.startsWith(prefix)) continue
      entries.delete(key)
      pending.delete(key)
      versions.set(key, (versions.get(key) || 0) + 1)
    }
    persist()
  }
  const load = (key, fetcher, { force = false } = {}) => {
    const ctx = ensureContext()
    if (!ctx.account || !ctx.credential) return Promise.resolve(null)
    if (!force) {
      if (pending.has(key)) return pending.get(key)
      const cached = peek(key)
      if (cached?.fresh) return Promise.resolve(cached.data)
    }
    const version = (versions.get(key) || 0) + 1
    versions.set(key, version)
    const current = () => ensureContext() === ctx && versions.get(key) === version
    const task = Promise.resolve().then(() => fetcher(ctx.credential)).then(data => {
      if (!current()) return null
      entries.delete(key)
      entries.set(key, { data, at: now() })
      while (entries.size > maxEntries) entries.delete(entries.keys().next().value)
      persist()
      return data
    }).catch(error => {
      if (current() && [401, 403, 404].includes(Number(error?.code || error?.response?.status))) {
        invalidate(key.startsWith('page:') ? key.slice(0, key.indexOf(':', 5) + 1) : key)
      }
      throw error
    }).finally(() => {
      if (current() && pending.get(key) === task) pending.delete(key)
    })
    pending.set(key, task)
    return task
  }
  return {
    peek, load, invalidate, flush,
    getNavigation: () => { ensureContext(); return JSON.parse(JSON.stringify(navigation)) },
    saveNavigation: value => { ensureContext(); navigation = JSON.parse(JSON.stringify(value)); persist() }
  }
}

// One background request at a time. Foreground navigation can join it via cache.load.
export function createSerialPrefetch({ run, canRun, delay = 350, keyOf = pageKey }) {
  const queue = new Map()
  let timer = null
  let running = false
  let stopped = false
  const schedule = () => {
    if (stopped || running || timer || !queue.size) return
    timer = setTimeout(tick, delay)
  }
  const tick = async () => {
    timer = null
    if (stopped || !queue.size) return
    if (!canRun()) { schedule(); return }
    const [key, query] = queue.entries().next().value
    queue.delete(key)
    running = true
    try { await run(query) } catch (_) { /* Speculative failures remain silent/retryable on demand. */ }
    finally { running = false; schedule() }
  }
  return {
    replace(queries) { queue.clear(); for (const query of queries) queue.set(keyOf(query), query); schedule() },
    stop() { stopped = true; queue.clear(); if (timer) clearTimeout(timer); timer = null }
  }
}
